import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_RED,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrUtilityProfitRemoveProviderModal from 'client/components/SrUtilityProfitRemoveProviderModal/SrUtilityProfitRemoveProviderModal';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import { isHomebuyerPartner } from 'common/utility/helpers';

const getFormFields = ({
  isNew,
  defaultNote,
  hasPartner,
  partnerSlug,
  utilityPartner,
  isAdmin = false,
}) => [
  {
    id: 'id',
    type: 'hidden',
  },
  showFieldIf(!isAdmin, {
    id: 'addressId',
    type: 'hidden',
  }),
  ...(isAdmin
    ? [
        {
          id: 'utilityTypeId',
          title: 'Utility Type',
          type: 'select',
          validations: ['required'],
          properties: {
            choices: [], // Will be populated by parent component
          },
        },
      ]
    : [
        {
          id: 'utilityTypeId',
          type: 'hidden',
        },
      ]),
  showFieldIf(!isAdmin, {
    id: 'utilityPartnerId',
    type: 'hidden',
  }),
  showFieldIf(isNew || isAdmin, {
    id: 'title',
    title: 'Company Name',
    type: 'short_text',
    validations: ['required'],
    properties: {
      placeholder: 'type company name here...',
    },
  }),
  showFieldIf(isNew || isAdmin, {
    id: 'url',
    title: 'URL to turn on utilities',
    type: 'short_text',
    validations: ['required', 'url'],
    properties: {
      placeholder: 'type URL here...',
    },
  }),
  showFieldIf(isNew || isAdmin, {
    id: 'phone',
    title: 'Customer Service Phone',
    type: 'phone',
    validations: ['required', 'phone'],
    properties: {
      placeholder: 'type phone here...',
    },
  }),
  showFieldIf(defaultNote && !isAdmin, {
    id: 'defaultNote',
    title: 'Default Notes',
    subtitle: !partnerSlug ? null : (
      <SrText font="greycliff" weight="400" fontSize="16px" color="grey400">
        You added this default note, go edit it&nbsp;
        <SrLink color="green300" to={`/utility-profit/${partnerSlug}/settings/tenant`}>
          here
        </SrLink>
      </SrText>
    ),
    type: 'long_text',
    properties: {
      disabled: true,
      placeholder: defaultNote,
      style: {
        height: '100px',
      },
    },
  }),
  showFieldIf(hasPartner && !isAdmin, {
    id: 'note',
    title: isHomebuyerPartner(utilityPartner) ? 'Note to client' : 'Note to tenant',
    label: '(optional)',
    subtitle: isHomebuyerPartner(utilityPartner)
      ? 'This note will show up to the client.'
      : 'This note will show up to the tenant.',
    type: 'long_text',
    properties: {
      placeholder: isHomebuyerPartner(utilityPartner)
        ? 'type note to client here...'
        : 'type note to tenant here...',
      style: {
        height: '140px',
      },
    },
  }),
];

const UPProviderUpsertForm = ({
  title,
  utilityType,
  utilityPartner,
  initialValues,
  addressDisplay,
  defaultNote,
  error,
  onCancel,
  onSubmit,
  onRemoveCoverage,
  partnerSlug,
  isAdmin = false,
  utilityTypes = [],
  isNew: isNewProp,
}) => {
  const [removeOptionConfirmOpen, setRemoveOptionConfirmOpen] = useState(false);
  const isNew = isNewProp !== undefined ? isNewProp : !initialValues.id;
  const hasPartner = !!initialValues.utilityPartnerId;

  const onRemoveCoverageClick = () => {
    setRemoveOptionConfirmOpen(true);
  };

  const onRemoveOptionCancel = () => {
    setRemoveOptionConfirmOpen(false);
  };

  const onRemoveOptionConfirm = () => {
    setRemoveOptionConfirmOpen(false);
    onRemoveCoverage();
  };

  const formFields = getFormFields({
    isNew,
    defaultNote,
    partnerSlug,
    hasPartner,
    isAdmin,
    utilityPartner,
  });

  // Update utility types choices if in admin mode
  if (isAdmin) {
    const utilityTypeField = formFields.find((field) => field.id === 'utilityTypeId');
    if (utilityTypeField) {
      utilityTypeField.properties.choices = utilityTypes.map((type) => ({
        label: type.label,
        value: type.id,
      }));
    }
  }

  const allHidden = formFields.every(({ type }) => type === 'hidden');

  return (
    <SrDarkModal onClose={onCancel}>
      <SrModalContent width={412} innerStyle={{ padding: 0 }}>
        <SrBox textAlign="center" borderBottom="2px solid #e9ebec" padding="20px 0">
          <SrText
            font="greycliff"
            weight="800"
            lineHeight="25px"
            fontSize="25px"
            display="block"
            paddingBottom="8px"
          >
            {title ||
              `${isNew ? 'Add' : 'Edit'} ${
                isAdmin ? 'Provider' : (utilityType?.label || '') + ' provider'
              }`}
          </SrText>
          {addressDisplay && (
            <SrText font="greycliff" weight="800" lineHeight="19px" fontSize="16px" display="block">
              {addressDisplay}
            </SrText>
          )}
        </SrBox>

        <SrBox padding="0 40px 40px">
          <Formik
            initialValues={createInitialValues({
              formFields,
              defaultValues: initialValues,
            })}
            enableReinitialize
            validate={createFormValidationFn(formFields)}
            onSubmit={(formValues) => {
              onSubmit(setNullEmptyValues(formValues));
            }}
            render={(formikBag) => {
              return (
                <Form>
                  {formFields.map((formField) => (
                    <SrFormikField
                      key={formField.id}
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  ))}
                  {error?.message && (
                    <SrBox padding="20px 0 0">
                      <SrErrorMessage>{error.message}</SrErrorMessage>
                    </SrBox>
                  )}
                  {!allHidden && (
                    <>
                      <SrBox padding="28px 0 0">
                        <SrButton
                          theme={SR_BUTTON_THEME_ADOBE_GREEN}
                          disabled={!formikBag.isValid || !formikBag.dirty}
                          heightType={SR_BUTTON_HEIGHT_MEDIUM}
                          hasBoxShadow
                          type="submit"
                        >
                          {`Save${isNew ? ' & Add' : ''}`}
                        </SrButton>
                      </SrBox>
                      <SrBox padding="20px 0 0">
                        <SrButton
                          theme={SR_BUTTON_THEME_ADOBE_GREY}
                          heightType={SR_BUTTON_HEIGHT_MEDIUM}
                          hasBoxShadow
                          onClick={onCancel}
                          type="button"
                        >
                          Cancel
                        </SrButton>
                      </SrBox>
                    </>
                  )}

                  {!isNew && onRemoveCoverage && (
                    <SrBox padding="20px 0 0">
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_RED}
                        heightType={SR_BUTTON_HEIGHT_MEDIUM}
                        hasBoxShadow
                        onClick={onRemoveCoverageClick}
                        type="button"
                      >
                        Remove Option
                      </SrButton>
                    </SrBox>
                  )}
                </Form>
              );
            }}
          />
        </SrBox>
        {removeOptionConfirmOpen && (
          <SrUtilityProfitRemoveProviderModal
            utilityPartner={utilityPartner}
            onRemoveOptionConfirm={onRemoveOptionConfirm}
            onRemoveOptionCancel={onRemoveOptionCancel}
          />
        )}
      </SrModalContent>
    </SrDarkModal>
  );
};

UPProviderUpsertForm.propTypes = {
  title: PropTypes.string,
  utilityType: PropTypes.object,
  utilityPartner: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  addressDisplay: PropTypes.string,
  defaultNote: PropTypes.string,
  error: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRemoveCoverage: PropTypes.func,
  partnerSlug: PropTypes.string,
  isAdmin: PropTypes.bool,
  utilityTypes: PropTypes.array,
  isNew: PropTypes.bool,
};

UPProviderUpsertForm.displayName = 'UPProviderUpsertForm';

export default UPProviderUpsertForm;
